/**
 * production canonical の vision-VAD evaluator (ADR 0003 update → issue#238 F41 本番反映)。
 * 内唇上下点の縦ギャップ vgap-2d (= `|lipLandmarks[13]−[14]|` の 2D 距離、 worker が返す raw landmark
 * から main 側で純粋幾何計算) の時系列を、 path-length 計算の**前段**で **trailing rolling median
 * (`medianFrames` 窓)** で平滑化し、 その平滑化系列の 1 階差分 path 長 / 経過秒 (= 口開閉の速度) で
 * 「発話 = 高頻度開閉」 / 「TP の sustained open ≠ 発話」 を弁別する。
 *
 * signal source の変遷 (ADR 0003 → F32 → F41):
 *   - 旧: ARKit `blendshapes.jawOpen` (= MediaPipe blendshape head の ML 推定値、 静止 sustain 中も
 *     frame 間で 0.02-0.05 oscillation が出て 1 frame spike で `openSince` reset = F30/F31 取りこぼし)
 *   - F32: raw 2D MAR ratio (`frame.mar` = `|P13−P14| / |P61−P291|` の縦/横比)。 ML 推定 noise を共有
 *     しないが、 分母 (口幅 `|P61−P291|`) が yaw 縮み + 頭部ジッタで偽 motion を注入し TP-oblique が退行
 *     (F35: 18→15)
 *   - F41 (本ファイルの現行): ratio をやめ vgap-2d (分母なし) に変え、 さらに path-length 前段で rolling
 *     median をかける。 vision-VAD の path-length に混入する 2 種の動き — (1) 本物の口開閉サイクル (発話、
 *     低周波)、 (2) landmark のフレーム間ジッタ (高周波ノイズ、 斜め顔で増幅) — のうち最高周波のジッタ
 *     だけを median で落とし発話エンベロープ (低周波) を残す = **周波数領域での分離**。 正規化 (割り算) と
 *     異なり**絶対スケールを保存する** (= 割り算しない) のが鍵。 issue#243 / F40 sweep が全軸 (TP 25/25 /
 *     TP-oblique 15→18 / misdet FP 29→17 / 全 fps robustness) で旧 default を支配する config を確定し、
 *     issue#238 の熟慮判断で本番採用 (確定値 = `mf7-w3000-t008` + `baselineWindowMs=8000`)
 *
 * issue#230 #1a (時間ベース化): 窓を frame 数固定 (`windowFrames=90` 等) から wall-clock ms に置換し、
 * 実 fps 非依存にした。 `push(vgap, timestamp)` で raw vgap を ring に積み、 motion は窓内 path 長 `Σ|Δ|`
 * / 経過秒 (= 単位/秒) で算出する (= sampling 密度が上がっても同じ実 motion なら速度は不変)。 hold は
 * **latch** に簡素化: 最後に閾値超過した時刻から `holdMs` 以内なら speaking=true を維持する。
 *
 * face-loss fallback (= 入力 vgap 不在のとき) は push skip (沈黙扱い)。 face-loss 中の fire は
 * detection-machine の `effectiveFaceDetected=false` で別途抑止される。
 *
 * 既定値 (issue#238 確定、 F40 sweep evidence。 motionThresholdPerSec は issue#276/F47 で 0.008→0.012):
 *   - `motionThresholdPerSec = 0.012`: vgap 絶対値は ratio の ~1/40 + median 平滑化のため、 旧 ratio
 *     経路の 0.33/s から大幅に下がる。 vgap は口幅で割らない絶対距離なので scale は顔距離/解像度依存。
 *     issue#276/F47 で 0.008→0.012 に引き上げ: 緩い/浅い開口 (slow/soft open) の低速口運動を「発話」 と
 *     誤 latch して detection-machine の `openSince` を reset し続ける経路を断ち、 yawn-then-open soft-open
 *     TP の取りこぼしを回復する (full eval で speech-bridge / vad-miss FP 非退行を確認)
 *   - `windowMs = 3000`: motion 平均窓 (据え置き)
 *   - `holdMs = 1000`: 発話間隙の埋め尺度 (据え置き、 latch)
 *   - `medianFrames = 7`: trailing rolling median の窓 (frame 数)。 F40 Step 0 で 3/5/7 を比較し、 7 が
 *     分離比 2.30 で最良 (= ジッタ除去 ⟺ 発話エンベロープ温存のバランス、 Nyquist トラップ回避)
 *
 * **eval substrate parity (#236 KTD1)**: eval substrate `mar-gradient-vad-v1` は production helper を
 * import せず独立実装を維持する。 本 evaluator の online 計算 (raw vgap ring + trailing median +
 * path長/秒 + latch) と substrate の offline stateless 再構成 (`buildMedfiltSamples` →
 * `judgeMotionSpeaking`) が同一 frame 列で per-frame 一致することを `dispatcher.test.ts` の数値 parity
 * test が EPS 厳密に担保する。 medfilt の causal (trailing) 性が parity の鍵: filtered[i] は raw[i-k+1..i]
 * のみに依存し未来 frame を見ないため、 production の incremental 計算と substrate の full-history replay が
 * 一致する。 そのため ring は windowMs に加え `medianFrames−1` frame のマージンを保持する (= 窓先頭 sample
 * の median が full history と同値になる、 §evict 参照)。
 */

const DEFAULT_MOTION_THRESHOLD_PER_SEC = 0.012;
const DEFAULT_WINDOW_MS = 3_000;
const DEFAULT_HOLD_MS = 1_000;
const DEFAULT_MEDIAN_FRAMES = 7;

declare global {
  interface Window {
    /**
     * E2E only: vision-VAD の speaking 判定を強制 override するための test hook。
     * 静止画 fixture (face-open.mjpeg 等) では MediaPipe が同一 landmark を返し続けるため
     * 実 motion 経由で speaking=true を再現できず、 UC-05 (発話中ミュート) の e2e 検証が
     * spec から表現できない。 spec が `page.addInitScript` でこの変数に boolean を set する
     * ことで、 production の detection-loop / detection-machine 経路は実 logic のまま通しつつ
     * vision-VAD の出力だけを差し替える。 production では設定されない (= undefined のとき
     * 通常 logic に fall through)。
     */
    __kuchipitaVisionVadForceSpeaking?: boolean;
  }
}

export type VisionVadOptions = {
  motionThresholdPerSec?: number;
  windowMs?: number;
  holdMs?: number;
  medianFrames?: number;
};

export type VisionVadEvaluator = {
  /**
   * raw vgap-2d (= `|lipLandmarks[13]−[14]|` の 2D 距離) を timestamp 付きで ring buffer に push。
   * undefined / non-finite な vgap / timestamp (= face-loss / landmark 欠落 frame) は skip。 push 後に
   * windowMs + (medianFrames−1) マージン分より古い sample を evict する (§冒頭 parity 参照)。
   */
  push: (vgap: number | undefined, timestamp: number) => void;
  /** 現在の ring buffer から effectiveIsSpeaking を判定する (medfilt + latch 含む)。 */
  evaluate: () => boolean;
};

type Sample = { vgap: number; t: number };

/**
 * worker の raw lipLandmarks から vgap-2d (= 内唇上下点 P13/P14 の 2D 距離 `sqrt(dx²+dy²)`) を導出する。
 * face 未検出 / landmark 欠落 / 非有限は undefined (= push skip = 沈黙扱い)。 worker が返す raw landmark
 * からの canonical な幾何導出なので main 側で行う (CLAUDE.md「canonical な導出は worker でよいが candidate
 * 判定 logic は main」)。 eval substrate `mar-gradient-vad-v2.ts` の `extractSignal('vgap-2d')` と同式 (parity)。
 */
export function computeVgap2d(
  lipLandmarks: Record<number, { x: number; y: number; z: number }> | undefined,
  faceDetected: boolean,
): number | undefined {
  if (!faceDetected) return undefined;
  const p13 = lipLandmarks?.[13];
  const p14 = lipLandmarks?.[14];
  if (p13 === undefined || p14 === undefined) return undefined;
  const vgap = Math.sqrt((p13.x - p14.x) ** 2 + (p13.y - p14.y) ** 2);
  return Number.isFinite(vgap) ? vgap : undefined;
}

/**
 * ソート中央値。 偶数窓は中央 2 値の平均 (= 一定入力で同値を保つ)。 空配列は 0。
 * vgap-2d-medfilt の低周波フィルタ本体 (= 単発スパイク = 高周波ジッタを中央値で吸収)。
 * eval `mar-gradient-vad-v2.ts` の `median` と同定義 (parity)。
 */
function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const sorted = values.slice().sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  if (sorted.length % 2 === 1) return sorted[mid] ?? 0;
  return ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
}

export function createVisionVadEvaluator(opts: VisionVadOptions = {}): VisionVadEvaluator {
  const motionThresholdPerSec = opts.motionThresholdPerSec ?? DEFAULT_MOTION_THRESHOLD_PER_SEC;
  const windowMs = opts.windowMs ?? DEFAULT_WINDOW_MS;
  const holdMs = opts.holdMs ?? DEFAULT_HOLD_MS;
  const medianFrames = opts.medianFrames ?? DEFAULT_MEDIAN_FRAMES;

  // session 境界では detection-loop ごと再生成されるため reset() メソッドは持たない (YAGNI)。
  // 同 detection-loop instance 内で PiP rAF 切替が起きても ring buffer の連続性を維持する。
  const samples: Sample[] = [];
  // latch state (フレーム跨ぎで保持): 最後に motion 閾値を超えた時刻。 holdMs 以内は speaking 維持。
  let lastSpeakingAt: number | null = null;
  // cold start 判定用の最初の有効 push 時刻。 時刻 evict で buffer span は頭打ちされるため、
  // evict されない first-seen を保持して「最初の観測から windowMs 経過したか」 で立ち上がりを見る。
  let firstSeenAt: number | null = null;

  return {
    push: (vgap, timestamp) => {
      if (typeof vgap !== "number" || !Number.isFinite(vgap)) return;
      if (!Number.isFinite(timestamp)) return;
      if (firstSeenAt === null) firstSeenAt = timestamp;
      samples.push({ vgap, t: timestamp });
      // 時刻 evict (+ medfilt マージン): 最新 timestamp から windowMs 窓に入る最古 sample を起点に、
      // さらに medianFrames−1 個古い sample まで残す。 これで windowMs 窓内の各 sample の trailing
      // median (= 直近 medianFrames raw) が full history と同値になり substrate と parity する
      // (§冒頭 parity)。 timestamp は worker frame の単調増加を前提 (= newest は末尾)。
      const cutoff = timestamp - windowMs;
      let firstInWindow = 0;
      while (firstInWindow < samples.length && (samples[firstInWindow]?.t ?? 0) < cutoff) {
        firstInWindow++;
      }
      const keepFrom = Math.max(0, firstInWindow - (medianFrames - 1));
      if (keepFrom > 0) samples.splice(0, keepFrom);
    },
    evaluate: () => {
      // E2E test-only override (declare global の JSDoc 参照)。 typeof で guard し、 production
      // で window 変数が未定義のときは即座に通常 logic に進む。
      if (typeof window !== "undefined" && typeof window.__kuchipitaVisionVadForceSpeaking === "boolean") {
        return window.__kuchipitaVisionVadForceSpeaking;
      }

      const newest = samples[samples.length - 1];
      if (newest === undefined || firstSeenAt === null) return false;
      const now = newest.t;

      // 立ち上がり中 (最初の観測から windowMs 未満) は安全側 false。 latch も更新しない
      // (substrate `judgeMotionSpeaking` の warmup early return と等価)。
      if (now - firstSeenAt < windowMs) return false;

      // windowMs 窓 [now − windowMs, now] に入る sample 群を path-length の node にする。 各 node の
      // 値は trailing median(medianFrames) で平滑化した vgap。 median の入力 raw は窓外マージン sample も
      // 含む (= substrate の full-history trailing median と同値)。 face-loss は push skip 済なので
      // samples は face-detected のみ = index 隣接が substrate の raw[] 隣接と一致する。
      const cutoff = now - windowMs;
      let pathLength = 0;
      let prevFiltered: number | null = null;
      for (let i = 0; i < samples.length; i++) {
        const s = samples[i];
        if (s === undefined || s.t < cutoff) continue;
        // trailing median 窓 [i − medianFrames + 1, i] (先頭は partial で許可、 substrate と同じ)。
        const lo = Math.max(0, i - medianFrames + 1);
        const windowVals: number[] = [];
        for (let j = lo; j <= i; j++) {
          const r = samples[j];
          if (r !== undefined) windowVals.push(r.vgap);
        }
        const filtered = median(windowVals);
        if (prevFiltered !== null) pathLength += Math.abs(filtered - prevFiltered);
        prevFiltered = filtered;
      }
      // 分母は固定 windowMs (= 実 buffer span ではない)。 face-loss 区間は push skip で path 長に
      // 寄与せず、 顔検出率が低い窓では速度を過小評価する = 保守側 (誤って発話判定して通知を抑止する
      // 方向には倒れない)。
      const speed = pathLength / (windowMs / 1_000);
      if (speed >= motionThresholdPerSec) {
        lastSpeakingAt = now;
      }

      // latch: 最後に閾値超過した時刻から holdMs 以内なら speaking=true を維持。
      return lastSpeakingAt !== null && now - lastSpeakingAt <= holdMs;
    },
  };
}
